import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  CalendarClock,
  CalendarDays,
  MapPin,
  Link as LinkIcon,
  Video,
  Users,
  Tag as TagIcon,
  ArrowRight,
  RefreshCw,
  Download,
  Search,
  Filter,
  Clock,
  Globe2,
  Building2,
  GraduationCap,
  Handshake,
  Megaphone,
} from "lucide-react";

/**
 * ONE DIGIIT – Events Hub (Single-file React page)
 * Stack: React + Tailwind + framer-motion + lucide-react (no extra deps)
 * Purpose: Unified, filterable, exportable view of events across all pillars
 *          (Communities, Academy, DIGIIT.AI Cloud, Catalyst, Partner Network).
 *
 * Data model: This page reads from a single array of events. For production, serve
 * a consolidated JSON at /api/events (aggregating from your five sites), or inject
 * a global window.INJECTED_EVENTS. This scaffold falls back to mockEvents below.
 */

// ================= SafeLink =================
function extractHashId(href) {
  if (!href || href[0] !== '#') return null;
  const after = href.slice(1);
  const id = after.split(/[?&]/)[0].trim();
  return id || null;
}
const SafeLink = ({ href, className = "", children, onClick }) => {
  const isExternal = /^(https?:)?\/\//.test(href);
  if (isExternal) {
    // Always open external links in a new tab to avoid sandbox/iframe blocks
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  const handleClick = (e) => {
    e.preventDefault();
    onClick && onClick(e);
    if (href && href.startsWith('#')) {
      const id = extractHashId(href);
      if (id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.location.hash = href;
      }
    }
  };
  return (
    <a href={href || '#'} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

// ================= UI atoms =================
const Section = ({ id, className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);
const Card = ({ className = "", children }) => (
  <div className={`rounded-2xl shadow-lg bg-white border border-slate-200 ${className}`}>{children}</div>
);
const H2 = ({ children, kicker, center }) => (
  <div className={`mb-8 ${center ? "text-center" : ""}`}>
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-fuchsia-600">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);
const Tag = ({ children }) => (
  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
    <TagIcon className="w-3.5 h-3.5" />{children}
  </span>
);

// ================= Icons per pillar =================
const PillarIcon = ({ pillar, className = "w-4 h-4" }) => {
  const map = {
    Communities: Globe2,
    Academy: GraduationCap,
    "DIGIIT.AI Cloud": Building2,
    Catalyst: Handshake,
    Partners: Megaphone,
  };
  const Comp = map[pillar] || CalendarDays;
  return <Comp className={className} />;
};

// ================= Mock / injected events =================
const mockEvents = [
  { id: "e1", title: "Community e‑Marketplace Launch", pillar: "Communities", start: "2025-09-20T10:00:00+05:30", end: "2025-09-20T12:00:00+05:30", city: "Coimbatore", online: false, reg: "#contact", tags: ["Launch", "CEM"], description: "Showcase of hyperlocal CEM with ONDC rails." },
  { id: "e2", title: "DPI 101: Train‑the‑Trainer", pillar: "Academy", start: "2025-09-18T16:00:00+05:30", end: "2025-09-18T18:00:00+05:30", city: "Virtual", online: true, reg: "#contact", tags: ["Workshop", "Certification"], description: "Onboarding faculty & trainers to curriculum." },
  { id: "e3", title: "Catalyst Onboarding Clinic", pillar: "Catalyst", start: "2025-09-06T11:00:00+05:30", end: "2025-09-06T12:30:00+05:30", city: "Virtual", online: true, reg: "#contact", tags: ["Clinic"], description: "Roles, standards, and impact dashboards." },
  { id: "e4", title: "CSR Roundtable: Women & DPI", pillar: "Partners", start: "2025-09-15T15:00:00+05:30", end: "2025-09-15T17:00:00+05:30", city: "Chennai", online: false, reg: "#contact", tags: ["CSR", "Women"], description: "Co‑creating impact programs with corporates." },
  { id: "e5", title: "ONDC + ULIP Integration Clinic", pillar: "DIGIIT.AI Cloud", start: "2025-10-03T14:00:00+05:30", end: "2025-10-03T16:00:00+05:30", city: "Bengaluru", online: false, reg: "#contact", tags: ["Tech", "POC"], description: "Live demo of logistics + commerce stack." },
];

// ================= Utils =================
const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const isPast = (iso) => new Date(iso).getTime() < Date.now();

// ICS export
function eventToICS(e) {
  const dt = (d) => new Date(d).toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
  const esc = (s) => String(s || "").replace(/[,;]/g, (m) => m === ';' ? '\\;' : '\\,');
  return [
    "BEGIN:VEVENT",
    `UID:${e.id}@onedigiit`,
    `DTSTAMP:${dt(e.start)}`,
    `DTSTART:${dt(e.start)}`,
    e.end ? `DTEND:${dt(e.end)}` : null,
    `SUMMARY:${esc(e.title)}`,
    `DESCRIPTION:${esc(e.description || "")}`,
    `LOCATION:${esc(e.online ? "Online" : e.city || "")}`,
    e.reg ? `URL:${esc(e.reg)}` : null,
    "END:VEVENT"
  ].filter(Boolean).join("\n");
}
function downloadICS(events) {
  const body = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//ONE DIGIIT//Events Hub//EN",
    ...events.map(eventToICS),
    "END:VCALENDAR"
  ].join("\n");
  const blob = new Blob([body], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `ONE-DIGIIT-Events-${new Date().toISOString().slice(0, 10)}.ics`; a.click();
  URL.revokeObjectURL(url);
}

// ================= NAV =================
const Nav = () => (
  <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
    <Section id={''} className="flex h-16 items-center justify-end">
      {/* <div className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
        <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-fuchsia-600 text-white font-black">E</span>
        Events Hub
      </div> */}
      <div className="hidden md:flex items-center gap-3">
        <SafeLink href="#filters" onClick={() => { }} className="text-sm text-slate-600 hover:text-slate-900">Filters</SafeLink>
        <button onClick={() => downloadICS((window as any).__EVENTS__ || mockEvents)} className="inline-flex items-center gap-2 rounded-full bg-fuchsia-600 text-white px-4 py-2 text-sm font-semibold hover:bg-fuchsia-700"><Download className="w-4 h-4" /> Export .ics</button>
      </div>
    </Section>
  </header>
);

// ================= HERO =================
const Hero = () => (
  <div className="relative overflow-hidden bg-gradient-to-b from-fuchsia-700 via-fuchsia-700 to-pink-700 text-white">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_85%_25%,rgba(255,255,255,0.06),transparent_35%)]" />
    <Section id={''} className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <div className="flex gap-2 mb-4 text-xs">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 backdrop-blur">Unified • Real‑time</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 backdrop-blur">Across Five Pillars</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">ONE DIGIIT Events Hub</h1>
          <p className="mt-4 text-lg md:text-xl text-pink-100 max-w-2xl">One calendar for Communities, Academy, DIGIIT.AI Cloud, Catalyst, and Partner Network — discover, register, and convert interest into impact.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#upcoming" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 font-semibold">Browse Upcoming <ArrowRight className="w-4 h-4" /></a>
            <a href="#filters" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3">Refine Filters</a>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}>
          <Card className="p-6 md:p-8 bg-white/95 text-slate-900">
            <div className="font-semibold">Today at a glance</div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div className="rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500">Total Upcoming</div>
                <div className="text-2xl font-black" id="metric-total">—</div>
              </div>
              <div className="rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500">This Month</div>
                <div className="text-2xl font-black" id="metric-month">—</div>
              </div>
            </div>
            <div className="mt-6 text-xs text-slate-500">* Metrics auto-calc from the unified feed below.</div>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

// ================= FILTER BAR =================
const Filters = ({ filters, setFilters, search, setSearch }) => (
  <Section id="filters" className="py-6">
    <Card className="p-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 border border-slate-300 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, city, tags…" className="outline-none text-sm" />
        </div>
        <div className="flex items-center gap-2 text-sm"><Filter className="w-4 h-4" /> Filters:</div>
        <select value={filters.pillar} onChange={(e) => setFilters(f => ({ ...f, pillar: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          {['All', 'Communities', 'Academy', 'DIGIIT.AI Cloud', 'Catalyst', 'Partners'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.type} onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          {['All', 'Online', 'In‑person'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.when} onChange={(e) => setFilters(f => ({ ...f, when: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          {['Upcoming', 'Past', 'All'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button onClick={() => { setFilters({ pillar: 'All', type: 'All', when: 'Upcoming' }); setSearch(''); }} className="ml-auto inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"><RefreshCw className="w-4 h-4" /> Reset</button>
      </div>
    </Card>
  </Section>
);

// ================= EVENT CARD =================
const EventCard = ({ e }) => (
  <Card className="p-5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-slate-500 text-sm"><CalendarClock className="w-4 h-4" /> {fmtDate(e.start)}</div>
      <Tag><PillarIcon pillar={e.pillar} /> {e.pillar}</Tag>
    </div>
    <div className="mt-2 font-semibold text-slate-900 text-lg">{e.title}</div>
    <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-600">
      <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {e.online ? 'Online' : e.city || '—'}</span>
      {e.online && <span className="inline-flex items-center gap-2"><Video className="w-4 h-4" /> Virtual</span>}
      {e.tags?.length > 0 && e.tags.map(t => <Tag key={t}>{t}</Tag>)}
    </div>
    <p className="mt-3 text-sm text-slate-700">{e.description}</p>
    <div className="mt-4 flex gap-2">
      {e.reg && <SafeLink href={e.reg} onClick={() => { }} className="rounded-full px-3 py-1.5 text-sm font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700">Register</SafeLink>}
      <button onClick={() => downloadICS([e])} className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Add to Calendar</button>
    </div>
  </Card>
);

// ================= UPCOMING & PAST =================
const EventLists = ({ events }) => {
  const upcoming: any = events.filter((e: any) => !isPast(e.start));
  const past: any = events.filter((e: any) => isPast(e.start));
  useEffect(() => {
    const total = upcoming.length;
    const month = upcoming.filter(e => new Date(e.start).getMonth() === new Date().getMonth() && new Date(e.start).getFullYear() === new Date().getFullYear()).length;
    const mt = document.getElementById('metric-total'); if (mt) mt.textContent = String(total);
    const mm = document.getElementById('metric-month'); if (mm) mm.textContent = String(month);
  }, [events]);
  return (
    <>
      <Section id="upcoming" className="py-10">
        <H2 kicker="Upcoming" center>What’s next</H2>
        {upcoming.length === 0 ? <div className="text-center text-sm text-slate-500">No upcoming events match your filters.</div> : (
          <div className="grid md:grid-cols-2 gap-4">
            {upcoming.sort(
              (a: any, b: any) =>
                new Date(a.start).getTime() - new Date(b.start).getTime()
            )
              .slice(0, 6).map(e => <EventCard key={e.id} e={e} />)}
          </div>
        )}
      </Section>
      <Section id="past" className="py-10">
        <H2 kicker="Past" center>Recent highlights</H2>
        {past.length === 0 ? <div className="text-center text-sm text-slate-500">No past events yet.</div> : (
          <div className="grid md:grid-cols-2 gap-4">
            {
              past.sort(
                (a: any, b: any) =>
                  new Date(b.start).getTime() - new Date(a.start).getTime()
              )
                .slice(0, 6).map(e => <EventCard key={e.id} e={e} />)
            }
          </div>
        )}
      </Section>
    </>
  );
};

// ================= DATA HOOK (inject or fetch later) =================
function useEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    // 1) If backend injects a unified feed into window.__EVENTS__, use it
    if (Array.isArray((window as any).__EVENTS__) && (window as any).__EVENTS__.length) { setEvents((window as any).__EVENTS__); return; }
    // 2) Optional: fetch('/api/events').then(r=>r.json()).then(setEvents).catch(()=>setEvents(mockEvents));
    // For now fallback to mock
    setEvents(mockEvents);
  }, []);
  return [events, setEvents];
}

// ================= PAGE =================
export default function EventsHubPage() {
  const [events]: any = useEvents();
  const [filters, setFilters] = useState({ pillar: 'All', type: 'All', when: 'Upcoming' });
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return (events || []).filter((e: any) => {
      if (filters.pillar !== 'All' && e.pillar !== filters.pillar) return false;
      if (filters.type !== 'All') {
        const wantOnline = filters.type === 'Online';
        if (!!e.online !== wantOnline) return false;
      }
      if (filters.when !== 'All') {
        const past = isPast(e.start);
        if (filters.when === 'Upcoming' && past) return false;
        if (filters.when === 'Past' && !past) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = [e.title, e.city, (e.tags || []).join(' '), e.description].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [events, filters, search]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Nav />
      <Hero />
      <Filters filters={filters} setFilters={setFilters} search={search} setSearch={setSearch} />
      <EventLists events={filtered} />
      <footer className="bg-white border-t border-slate-200 text-slate-600">
        <Section id={''} className="py-10">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="text-sm"><b>ONE DIGIIT Events Hub</b><div className="text-slate-500">Unified calendar for Communities • Academy • DIGIIT.AI • Catalyst • Partners</div></div>
            <div className="text-sm">Data feed: <code>window.__EVENTS__</code> or <code>/api/events</code></div>
            <div className="text-right"><button onClick={() => downloadICS(filtered)} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"><Download className="w-4 h-4" /> Export Filtered .ics</button></div>
          </div>
        </Section>
      </footer>
    </div>
  );
}
